import { useEffect, useRef } from "react";
import axios from "axios";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in";

/**
 * Custom hook for auto-polling payment status
 * @param {Object} options - Configuration options
 * @param {Array<string>|string} options.paymentLinkIds - Payment link IDs to poll (array or single string)
 * @param {Function} options.onPaymentDetected - Callback when payment is detected as paid
 * @param {boolean} options.enabled - Whether polling is enabled (default: true)
 * @param {number} options.interval - Polling interval in milliseconds (default: 30000)
 */
export const usePaymentStatusPolling = ({
  paymentLinkIds,
  onPaymentDetected,
  enabled = true,
  interval = 20000,
} = {}) => {
  const pollIntervalRef = useRef(null);
  const callbackRef = useRef(onPaymentDetected);
  const paymentDetectedRef = useRef(false);

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = onPaymentDetected;
  }, [onPaymentDetected]);

  useEffect(() => {
    // Reset detection flag when enabled changes
    if (enabled) {
      paymentDetectedRef.current = false;
    }
    
    // Return early if polling is disabled
    if (!enabled) {
      return;
    }

    // Normalize paymentLinkIds to array
    const linkIds = Array.isArray(paymentLinkIds)
      ? paymentLinkIds
      : paymentLinkIds
        ? [paymentLinkIds]
        : [];

    // Return early if no payment link IDs
    if (linkIds.length === 0) {
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const token = localStorage.getItem("prima_session");
        if (!token) return;

        // Poll each payment link
        for (const linkId of linkIds) {
          try {
            const response = await axios.get(
              `${SERVER}/payments/status/${linkId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.paid) {
              // Prevent multiple detections
              if (paymentDetectedRef.current) {
                return;
              }
              paymentDetectedRef.current = true;

              // Clear polling interval immediately
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }

              // Call the callback with payment link ID
              if (callbackRef.current) {
                await callbackRef.current(linkId);
              }

              return;
            }
          } catch (error) {
            console.error(`Error polling payment ${linkId}:`, error.message);
          }
        }
      } catch (error) {
        console.error("Error in payment status polling:", error);
      }
    };

    // Poll immediately on mount
    pollPaymentStatus();

    // Start polling with specified interval
    pollIntervalRef.current = setInterval(pollPaymentStatus, interval);

    // Cleanup function
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [JSON.stringify(paymentLinkIds), enabled, interval]);

  // Return a manual stop function if needed
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  return { stopPolling };
};
